/**
 * AuditTeamPanel — who is running this audit. A lead auditor (accountable)
 * and co-auditors, assignable from the org's compliance users. Feeds the
 * Reporting tab's Auditor filter and the per-lead workload rollup.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

export default function AuditTeamPanel({ auditId, audit, onChanged }) {
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const lead = audit?.lead_auditor_id || '';
  const team = Array.isArray(audit?.team_user_ids) ? audit.team_user_ids : [];

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/audit/team-members`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive && d.success) setMembers(d.members || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const patch = async (body) => {
    setSaving(true); setSaved(false);
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); onChanged?.(); }
    } finally { setSaving(false); }
  };

  const toggleTeam = (id) => {
    const next = team.includes(id) ? team.filter(t => t !== id) : [...team, id];
    patch({ team_user_ids: next });
  };

  const name = (id) => members.find(m => m.id === id)?.name || members.find(m => m.id === id)?.email || '';

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Audit team</div>
        {saving && <span className="text-[10px] text-slate-400">Saving…</span>}
        {saved && <span className="text-[10px] font-semibold text-emerald-600">✓ Saved</span>}
      </div>
      <p className="text-[11px] text-slate-400 mb-3">The lead is accountable for this audit. Assignments feed the Reporting tab's Auditor filter.</p>

      <label className="block mb-3">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Lead auditor</span>
        <select value={lead} disabled={saving}
          onChange={e => patch({ lead_auditor_id: e.target.value || null })}
          className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700 bg-white">
          <option value="">Unassigned</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
        </select>
      </label>

      <div>
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Co-auditors</span>
        {members.filter(m => m.id !== lead).length === 0 && (
          <p className="text-[11px] text-slate-300">No other compliance users to assign.</p>
        )}
        <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
          {members.filter(m => m.id !== lead).map(m => (
            <label key={m.id} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={team.includes(m.id)} disabled={saving}
                onChange={() => toggleTeam(m.id)} className="accent-orange-600" />
              <span>{m.name || m.email}</span>
            </label>
          ))}
        </div>
        {team.length > 0 && (
          <p className="text-[10px] text-slate-400 mt-2">{team.length} co-auditor{team.length === 1 ? '' : 's'}: {team.map(name).filter(Boolean).join(', ')}</p>
        )}
      </div>
    </div>
  );
}
